<div class="billing-checkout-page account-settings-page">
  @if (isInitialLoading) {
    <section class="page-card card">
      <div class="card-body py-5 text-center">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 mb-0">Preparing checkout...</p>
      </div>
    </section>
  } @else if (hasInitialLoadError) {
    <app-api-load-error
      title="Could not prepare checkout"
      message="Some error occurred while loading the selected plan and billing profile."
      retryLabel="Retry"
      (retry)="loadCheckoutState()"
    ></app-api-load-error>
  } @else if (!selectedPlan || !currentUser) {
    <section class="page-card card">
      <div class="card-body">
        <h3 class="section-title bw-title-with-info">
          Plan not found
          <app-info-tooltip compact placement="right" text="The selected plan is unavailable. Return to billing and choose a plan again."></app-info-tooltip>
        </h3>
        <a class="btn btn-primary" routerLink="/billing/plans">Back to plans</a>
      </div>
    </section>
  } @else {
    <div class="billing-checkout-grid">
      <section class="page-card card">
        <div class="card-body d-grid gap-3">
          <div>
            <h3 class="section-title mb-0 bw-title-with-info">
              Checkout summary
              <app-info-tooltip compact placement="right" text="Review your current and selected plans, plus how the change will be applied before continuing."></app-info-tooltip>
            </h3>
          </div>

          @if (pageMessage) {
            <div class="alert alert-success mb-0">{{ pageMessage }}</div>
          }
          @if (checkoutError) {
            <div class="alert alert-danger mb-0">{{ checkoutError }}</div>
          }

          <div class="billing-summary-grid">
            <article class="billing-summary">
              <span class="billing-summary__label">Current plan</span>
              <strong>{{ currentUser.plan.name }}</strong>
              <span>{{ formatPlanPrice(currentUser.plan) }}</span>
            </article>

            <article class="billing-summary billing-summary--next">
              <span class="billing-summary__label">Selected plan</span>
              <strong>{{ selectedPlan.name }}</strong>
              <span>{{ formatPlanPrice(selectedPlan) }}</span>
            </article>
          </div>

          <div class="billing-checkout__status">
            <span class="billing-summary__label">What happens next</span>
            <p class="mb-0">{{ summaryMessage }}</p>
          </div>

          @if (billing?.scheduledChangeAction) {
            <div class="alert alert-warning mb-0">
              There is already a scheduled change on this subscription. Use the Paddle billing portal to manage it first.
            </div>
          }

          <ul class="list-unstyled billing-checkout__features mb-0">
            <li>
              Active websites:
              <strong>
                @if (selectedPlan.entitlements.maxActiveWebsites === null) {
                  Unlimited
                } @else {
                  {{ selectedPlan.entitlements.maxActiveWebsites }}
                }
              </strong>
            </li>
            <li>
              Team members:
              <strong>
                @if (selectedPlan.entitlements.maxTeamMembers === null) {
                  Unlimited
                } @else {
                  {{ selectedPlan.entitlements.maxTeamMembers }}
                }
              </strong>
            </li>
            <li>Custom domains: <strong>{{ selectedPlan.entitlements.allowCustomDomain ? 'Included' : 'Not included' }}</strong></li>
            <li>Export access: <strong>{{ selectedPlan.entitlements.allowExport ? 'Included' : 'Not included' }}</strong></li>
            <li>Publishing: <strong>{{ selectedPlan.entitlements.allowPublish ? 'Included' : 'Upgrade required before publishing' }}</strong></li>
            <li>Support: <strong>{{ selectedPlan.entitlements.supportLevel }}</strong></li>
          </ul>

          <div class="d-flex flex-wrap gap-2">
            <a class="btn btn-outline-secondary" routerLink="/billing/plans">Back to plans</a>
            @if (canOpenPortal) {
              <button type="button" class="btn btn-outline-secondary" [disabled]="isPortalBusy" (click)="openBillingPortal()">
                @if (!isPortalBusy) {
                  <span>Open billing portal</span>
                } @else {
                  <span>Opening...</span>
                }
              </button>
            }
          </div>
        </div>
      </section>

      <section class="page-card card">
        <div class="card-body d-grid gap-3">
          @if (isInlineCheckoutMode) {
            <div>
              <h3 class="section-title mb-0 bw-title-with-info">
                Secure checkout
                <app-info-tooltip compact placement="right" text="Paddle powers the embedded payment form below and securely handles payment details."></app-info-tooltip>
              </h3>
            </div>

            <label class="billing-consent">
              <input
                type="checkbox"
                [(ngModel)]="consentAccepted"
                name="billing_consent"
              />
              <span>{{ consentText }}</span>
            </label>

            @if (!isCheckoutMounted) {
              <div class="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  class="btn btn-primary"
                  (click)="launchInlineCheckout()"
                  [disabled]="isCheckoutMounting || isCheckoutCompleting"
                >
                  @if (!isCheckoutMounting) {
                    <span>Load secure checkout</span>
                  } @else {
                    <span>Loading...</span>
                  }
                </button>
              </div>
            }

            <div class="billing-paddle-frame" [class.is-visible]="isCheckoutMounted || isCheckoutMounting">
              <div [id]="checkoutFrameTarget"></div>
            </div>

            @if (isCheckoutCompleting) {
              <div class="alert alert-info mb-0">
                Payment completed. Finalizing the subscription and refreshing your plan access...
              </div>
            }
          } @else if (isDirectChangeMode) {
            <div>
              <h3 class="section-title mb-0 bw-title-with-info">
                Apply plan change
                <app-info-tooltip compact placement="right" text="This plan change can be applied directly without opening a new checkout session."></app-info-tooltip>
              </h3>
            </div>

            <div class="billing-checkout__status">
              <span class="billing-summary__label">Change behavior</span>
              <p class="mb-0">{{ summaryMessage }}</p>
            </div>

            <div class="d-flex flex-wrap gap-2">
              <button
                type="button"
                [class]="directActionClass"
                (click)="confirmDirectChange()"
                [disabled]="isActionBusy"
              >
                @if (!isActionBusy) {
                  <span>{{ directActionLabel }}</span>
                } @else {
                  <span>Processing...</span>
                }
              </button>
            </div>
          } @else if (checkoutMode === 'subscription_missing') {
            <div>
              <h3 class="section-title mb-0 bw-title-with-info">
                Subscription mapping required
                <app-info-tooltip compact placement="right" text="This account has a paid local plan but no active Paddle subscription mapping yet."></app-info-tooltip>
              </h3>
            </div>

            <div class="alert alert-warning mb-0">
              Open the billing portal if one exists, or contact support to repair the billing mapping before changing plans.
            </div>

            @if (canOpenPortal) {
              <button type="button" class="btn btn-outline-secondary" [disabled]="isPortalBusy" (click)="openBillingPortal()">
                @if (!isPortalBusy) {
                  <span>Open billing portal</span>
                } @else {
                  <span>Opening...</span>
                }
              </button>
            }
          } @else {
            <div>
              <h3 class="section-title mb-0 bw-title-with-info">
                No checkout needed
                <app-info-tooltip compact placement="right" text="The selected plan is already active on this account."></app-info-tooltip>
              </h3>
            </div>
          }
        </div>
      </section>
    </div>
  }
</div>
